/**
 * AdminInterests — 2-tier Category & Sub-category management page.
 * Sidebar label: "Interests"
 * Internal terminology: Categories → Sub-categories
 *
 * Exports InterestIcon for use by other components (backward compat).
 */
export { InterestIcon } from './InterestIcon';

import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import {
  getInterests,
  createCategory,
  updateCategory,
  deleteCategory,
} from '../../api/api';
import { InterestIcon } from './InterestIcon';

// ── Icon picker (Lucide names only) ──────────────────────────────────────────
const ICON_OPTIONS = [
  'Recycle','Droplets','Leaf','School','Coffee','Flag','Users','Building2',
  'HeartPulse','Trash','Trash2','Package','Zap','ShoppingBag','FlameKindling',
  'Waves','CloudRain','Mountain','Flower2','TreeDeciduous','Bird','BookOpen',
  'Star','UserCheck','Heart','Mic','Car','Bike','Camera','Baby','Home',
  'Sprout','TreePine','Sun','GraduationCap','MapPin','Globe','Lightbulb',
];

const IconPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-9 h-9 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
        data-testid="icon-picker-btn"
      >
        {value ? (
          <InterestIcon icon={value} size={16} className="text-gray-600" />
        ) : (
          <span className="text-xs text-gray-400">icon</span>
        )}
      </button>
      {open && (
        <div className="absolute z-20 left-0 top-10 bg-white border border-gray-200 rounded-xl shadow-lg p-2 flex flex-wrap gap-1 w-52">
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false); }}
            className="w-7 h-7 rounded flex items-center justify-center hover:bg-gray-100 text-xs text-gray-400 border border-dashed border-gray-300"
          >
            —
          </button>
          {ICON_OPTIONS.map((ic) => (
            <button
              key={ic}
              type="button"
              onClick={() => { onChange(ic); setOpen(false); }}
              className={`w-7 h-7 rounded flex items-center justify-center hover:bg-green-50 ${value === ic ? 'bg-green-100 ring-1 ring-green-500' : ''}`}
              title={ic}
            >
              <InterestIcon icon={ic} size={14} className="text-gray-600" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Inline add/edit form ─────────────────────────────────────────────────────
const InlineForm = ({ initial = {}, onSave, onCancel, label }) => {
  const [name, setName] = useState(initial.name || '');
  const [icon, setIcon] = useState(initial.icon || '');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try { await onSave({ name: name.trim(), icon }); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
      <IconPicker value={icon} onChange={setIcon} />
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel(); }}
        placeholder={label}
        className="flex-1 text-sm px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-green-500"
        data-testid="interest-name-input"
      />
      <button
        type="button"
        onClick={submit}
        disabled={saving || !name.trim()}
        className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        data-testid="interest-save-btn"
      >
        <Check size={14} />
      </button>
      <button type="button" onClick={onCancel} className="p-1.5 text-gray-400 hover:text-gray-600">
        <X size={14} />
      </button>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const AdminInterests = () => {
  const qc = useQueryClient();

  const { data: allCategories = [] } = useQuery({
    queryKey: ['interests'],
    queryFn: async () => (await getInterests()).data,
  });

  // Separate parents and sub-categories
  const parents = useMemo(
    () => allCategories.filter((c) => !c.parent_id),
    [allCategories],
  );
  const subsByParent = useMemo(() => {
    const map = {};
    allCategories.filter((c) => c.parent_id).forEach((c) => {
      (map[c.parent_id] = map[c.parent_id] || []).push(c);
    });
    return map;
  }, [allCategories]);

  // Separate new 2-tier categories from legacy flat categories (no sub-categories)
  const newParents    = useMemo(() => parents.filter((p) => (subsByParent[p.id] || []).length > 0), [parents, subsByParent]);
  const legacyParents = useMemo(() => parents.filter((p) => (subsByParent[p.id] || []).length === 0), [parents, subsByParent]);

  const [expanded, setExpanded] = useState({});
  const [adding, setAdding]     = useState(null); // 'parent' | parentId
  const [editing, setEditing]   = useState(null); // { id, name, icon, parent_id }
  const [deleting, setDeleting] = useState(null); // id

  const invalidate = () => qc.invalidateQueries({ queryKey: ['interests'] });

  const handleAddParent = async ({ name, icon }) => {
    await createCategory({ name, icon, parent_id: null });
    setAdding(null);
    invalidate();
  };

  const handleAddSub = async (parentId, { name, icon }) => {
    await createCategory({ name, icon, parent_id: parentId });
    setAdding(null);
    invalidate();
  };

  const handleEdit = async ({ name, icon }) => {
    await updateCategory(editing.id, { name, icon });
    setEditing(null);
    invalidate();
  };

  const handleDelete = async (id) => {
    await deleteCategory(id);
    setDeleting(null);
    invalidate();
  };

  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // ── Render a single parent category card ─────────────────────────────────
  const renderParent = (parent, isLegacy = false) => {
    const subs = subsByParent[parent.id] || [];
    const isExpanded = !!expanded[parent.id];
    const isEditing  = editing?.id === parent.id;

    return (
      <div key={parent.id} className={`border rounded-xl overflow-hidden ${isLegacy ? 'border-gray-200 opacity-75' : 'border-gray-200'}`}>
        {/* Parent row */}
        {isEditing ? (
          <div className="p-2">
            <InlineForm initial={editing} label="Category name" onSave={handleEdit} onCancel={() => setEditing(null)} />
          </div>
        ) : (
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50">
            <button
              onClick={() => toggleExpand(parent.id)}
              className="flex items-center gap-2.5 flex-1 min-w-0"
              data-testid={`category-expand-${parent.id}`}
            >
              {isExpanded
                ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
                : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
              }
              <InterestIcon icon={parent.icon} size={16} className="text-gray-500 flex-shrink-0" />
              <span className="font-semibold text-gray-800 text-sm truncate">{parent.name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">({subs.length} sub-categories)</span>
            </button>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => setEditing({ id: parent.id, name: parent.name, icon: parent.icon || '', parent_id: null })}
                className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                data-testid={`edit-category-${parent.id}`}
              >
                <Pencil size={13} />
              </button>
              <button
                onClick={() => setDeleting(parent.id)}
                className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                data-testid={`delete-category-${parent.id}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        )}

        {/* Delete confirm */}
        {deleting === parent.id && (
          <div className="px-4 py-2 bg-red-50 border-t border-red-100 flex items-center gap-3 text-sm">
            <span className="text-red-700 flex-1">Delete this category{subs.length > 0 ? ` and all ${subs.length} sub-categories` : ''}?</span>
            <button onClick={() => handleDelete(parent.id)} className="px-3 py-1 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700" data-testid="confirm-delete-btn">Delete</button>
            <button onClick={() => setDeleting(null)} className="text-gray-500 hover:text-gray-700 text-xs">Cancel</button>
          </div>
        )}

        {/* Sub-categories */}
        {isExpanded && (
          <div className="border-t border-gray-100">
            {subs.map((sub) => {
              const isEditingSub  = editing?.id === sub.id;
              const isDeletingSub = deleting === sub.id;
              return (
                <div key={sub.id} className="border-b border-gray-50 last:border-0">
                  {isEditingSub ? (
                    <div className="p-2 pl-10">
                      <InlineForm initial={editing} label="Sub-category name" onSave={handleEdit} onCancel={() => setEditing(null)} />
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 pl-10 pr-4 py-2.5">
                      <InterestIcon icon={sub.icon || parent.icon} size={13} className="text-gray-400 flex-shrink-0" />
                      <span className="flex-1 text-sm text-gray-700">{sub.name}</span>
                      {!sub.icon && <span className="text-xs text-gray-300 italic">no icon</span>}
                      <button
                        onClick={() => setEditing({ id: sub.id, name: sub.name, icon: sub.icon || '', parent_id: sub.parent_id })}
                        className="p-1 text-gray-300 hover:text-blue-500 rounded"
                        data-testid={`edit-sub-${sub.id}`}
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => setDeleting(sub.id)}
                        className="p-1 text-gray-300 hover:text-red-400 rounded"
                        data-testid={`delete-sub-${sub.id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                  {isDeletingSub && (
                    <div className="px-10 py-2 bg-red-50 flex items-center gap-3 text-xs">
                      <span className="text-red-700 flex-1">Delete "{sub.name}"?</span>
                      <button onClick={() => handleDelete(sub.id)} className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700">Delete</button>
                      <button onClick={() => setDeleting(null)} className="text-gray-500">Cancel</button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add sub-category */}
            <div className="pl-10 pr-4 py-2">
              {adding === parent.id ? (
                <InlineForm
                  label="Sub-category name"
                  onSave={(data) => handleAddSub(parent.id, data)}
                  onCancel={() => setAdding(null)}
                />
              ) : (
                <button
                  onClick={() => { setAdding(parent.id); setExpanded((p) => ({ ...p, [parent.id]: true })); }}
                  className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium py-0.5"
                  data-testid={`add-sub-${parent.id}`}
                >
                  <Plus size={12} /> Add Sub-category
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Categories &amp; Sub-categories</h2>
          <p className="text-sm text-gray-500">Manage interest taxonomy. Sub-categories appear as interest tags across the platform.</p>
        </div>
        <button
          onClick={() => setAdding('parent')}
          className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
          data-testid="add-category-btn"
        >
          <Plus size={15} /> Add Category
        </button>
      </div>

      {/* Add parent form */}
      {adding === 'parent' && (
        <InlineForm label="Category name" onSave={handleAddParent} onCancel={() => setAdding(null)} />
      )}

      {/* New 2-tier categories */}
      <div className="space-y-2">
        {newParents.map((parent) => renderParent(parent))}
      </div>

      {/* Legacy flat categories — will be removed after Step 3/4 data migration */}
      {legacyParents.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs font-semibold text-gray-400 uppercase tracking-wide py-2 flex items-center gap-2 select-none list-none">
            <ChevronRight size={12} />
            Legacy Interest Tags ({legacyParents.length}) — to be removed after data migration
          </summary>
          <div className="space-y-1 mt-2">
            {legacyParents.map((parent) => renderParent(parent, true))}
          </div>
        </details>
      )}

      {newParents.length === 0 && legacyParents.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm">
          No categories yet. Click "Add Category" to create the first one.
        </div>
      )}
    </div>
  );
};

export default AdminInterests;
